import { ComboboxData, Group, LoadingOverlay, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { dateFormat } from '../../../utilities/dataFormat';
import { useMutation } from '@tanstack/react-query';
import { getCategoryList } from '../../../services/navbar';
import { showNotification } from '../../../utilities/mantineNotification';
import Button from '../../elements/Button';
import { Data } from './ModalFilter';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Loader from '../Loader';
import { HiOutlineDownload } from 'react-icons/hi';

const labelPeriod = 'Select Period';
const labelCategory = 'Select Category List';

interface FormFilterProps {
  onFilter: (data: any) => void;
  onReset?: () => void;
  data?: Data;
  type?: 'global' | 'inside-chart';
  downloadable?: boolean;
  loading?: boolean;
  onDownload?: () => void;
}

const FormFilter: React.FC<FormFilterProps> = ({
  downloadable,
  onDownload,
  onFilter,
  onReset,
  loading,
  data,
  type = 'global'
}) => {
  const [startDatePeriod, setStartDatePeriod] = useState<string>(null);
  const [endDatePeriod, setEndDatePeriod] = useState<string>(null);
  const [openCustom, setOpenCustom] = useState<boolean>(false);
  const [options] = useState<ComboboxData>([
    {
      label: 'Last 7 Days',
      value: '7'
    },
    {
      label: 'Last 28 Days',
      value: '28'
    },
    {
      label: 'Last 3 Months',
      value: '3'
    },
    {
      label: 'Last 6 Months',
      value: '6'
    },
    {
      label: 'Last 12 Months',
      value: '12'
    },
    {
      label: 'Custom Period',
      value: 'custom'
    }
  ]);
  const [categories, setCategories] = useState<ComboboxData>([]);

  const fetchCategory = useMutation({
    mutationFn: () => getCategoryList(),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  useEffect(() => {
    if (type === 'global') {
      fetchCategory.mutate();
    }
  }, [type]);

  useEffect(() => {
    if (fetchCategory?.data?.data?.data) {
      const data = fetchCategory?.data?.data?.data?.map((item: any) => {
        return {
          label: item.categoryName,
          value: item.categoryId
        };
      });
      setCategories(data);
    }
  }, [fetchCategory?.data?.data?.data]);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<any>({
    defaultValues: {
      startDate: data?.startDate || '',
      endDate: data?.endDate || '',
      categoryId: data?.categoryId || ''
    }
  });

  const handleSelect = (value: any, field: any) => {
    field.onChange(value);
    const currentDate = dayjs();
    const days = ['7', '28'];
    const months = ['3', '6', '12'];
    const inDays = days.includes(value);
    const inMonths = months.includes(value);

    if (inDays || inMonths) {
      setOpenCustom(false);

      const _startDate = dayjs(currentDate)
        .subtract(Number(value), inDays ? 'day' : 'month')
        .format(dateFormat('filter-format'));
      const _endDate = dayjs(currentDate).format(dateFormat('filter-format'));

      setStartDatePeriod(_startDate);
      setEndDatePeriod(_endDate);
      setValue('startDate', _startDate);
      setValue('endDate', _endDate);
    }
    if (!inDays && !inMonths && value === 'custom') {
      setOpenCustom(true);
    }
  };

  useEffect(() => {
    if (data) {
      setOpenCustom(true);
    }
  }, [data]);

  const handleSubmitForm: SubmitHandler<any> = (values) => {
    onFilter(values);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <Controller
          control={control}
          render={({ field }) => (
            <Select
              label={labelPeriod}
              data={options}
              placeholder={labelPeriod}
              onChange={(e) => handleSelect(e, field)}
            />
          )}
          name="period"
        />
        {openCustom && (
          <Group gap={16} justify="space-between">
            <Controller
              control={control}
              render={({ field }) => (
                <DatePickerInput
                  flex={1}
                  label="Start Period"
                  placeholder="Select start period"
                  rightSection={<FiCalendar />}
                  value={
                    startDatePeriod
                      ? new Date(startDatePeriod)
                      : field.value
                      ? new Date(field.value)
                      : null
                  }
                  onChange={(date) => {
                    setStartDatePeriod(
                      dayjs(date).format(dateFormat('filter-format'))
                    );
                    field.onChange(
                      dayjs(date).format(dateFormat('filter-format'))
                    );
                  }}
                  defaultValue={new Date(data?.startDate)}
                  maxDate={new Date()}
                  // {...field}
                />
              )}
              name="startDate"
            />
            <Controller
              control={control}
              render={({ field }) => (
                <DatePickerInput
                  flex={1}
                  label="End Period"
                  placeholder="Select end period"
                  rightSection={<FiCalendar />}
                  minDate={new Date(startDatePeriod)}
                  value={
                    endDatePeriod
                      ? new Date(endDatePeriod)
                      : field.value
                      ? new Date(field.value)
                      : null
                  }
                  onChange={(date) => {
                    setEndDatePeriod(
                      dayjs(date).format(dateFormat('filter-format'))
                    );
                    field.onChange(
                      dayjs(date).format(dateFormat('filter-format'))
                    );
                  }}
                  defaultValue={new Date(data?.endDate)}
                  maxDate={new Date()}
                  // {...field}
                />
              )}
              name="endDate"
            />
          </Group>
        )}
        {type === 'global' && (
          <div></div>
          // <Controller
          //   control={control}
          //   render={({ field }) => (
          //     <Select
          //       label={labelCategory}
          //       data={categories}
          //       placeholder={labelCategory}
          //       disabled={fetchCategory.isLoading}
          //       {...field}
          //     />
          //   )}
          //   name="categoryId"
          // />
        )}

        <Group justify="flex-end" pt={24}>
          {downloadable && (
            <Button
              type="button"
              variant="transparent"
              leftSection={<HiOutlineDownload />}
              onClick={onDownload}
            >
              Download Data Filter
            </Button>
          )}
          {type === 'global' && (
            <Button type="button" variant="transparent" onClick={onReset}>
              Reset Filter
            </Button>
          )}
          <Button type="submit">Apply Filter</Button>
        </Group>
      </form>
      <LoadingOverlay visible={loading} />
      <LoadingOverlay
        visible={fetchCategory.isLoading && type === 'global'}
        loaderProps={{ children: <Loader message="Fetching category..." /> }}
      />
    </>
  );
};

export default FormFilter;
